import React, { useState } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  LinearProgress,
  Typography,
  Paper
} from '@mui/material';
import { fetchExhibitorProducts } from '../../../../Reducer/exhibitorProductSlice';

const CsvUploader = () => {
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  const dispatch = useDispatch();

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setStatus('');
    setProgress(0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      setStatus('Please select a file.');
      toast.warning('Please select a file.');
      return;
    }

    const formData = new FormData();
    formData.append('csv', file);

    setLoading(true);
    setStatus('');

    try {
      const response = await axios.post('/api/update-price-stock-via-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setProgress(percent);
        },
      });

      toast.success('File uploaded successfully!');
      setStatus(`Success: ${response.data.message}, updated data will be reflect with in 3-5 minutes.`);

      dispatch(
        fetchExhibitorProducts({
          page: 1,
          limit: 10,
          filters: {},
        })
      );
    } catch (error) {
      const errorMsg =
        error.response?.data?.message || 'Something went wrong during upload.';
      setStatus(`Error: ${errorMsg}`);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  return (
    <Paper
      elevation={4}
      sx={{
        p: 3,
        maxWidth: 420,
        mx: 'auto',
        mt: 5,
        borderRadius: 3,
        backgroundColor: '#fff',
        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.06)',
        transition: 'box-shadow 0.3s ease',
        '&:hover': {
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.15), 0 10px 10px -5px rgb(0 0 0 / 0.1)',
        },
      }}
    >
      <Typography
        variant="h6"
        gutterBottom
        fontWeight="600"
        color="primary.main"
        sx={{ letterSpacing: 0.5 }}
      >
        Upload Product CSV
      </Typography>

      <form onSubmit={handleSubmit}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Button
            variant="outlined"
            component="label"
            size="medium"
            sx={{
              minWidth: 0,
              fontSize: '0.875rem',
              px: 3,
              py: 1,
              borderStyle: 'dashed',
              borderColor: '#1976d2',
              color: '#1976d2',
              fontWeight: '600',
              boxShadow: '0 2px 6px rgb(25 118 210 / 0.2)',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: '#e3f2fd',
                borderColor: '#115293',
                boxShadow: '0 6px 12px rgb(25 118 210 / 0.4)',
                transform: 'scale(1.05)',
              },
            }}
          >
            📁 Choose File
            <input
              type="file"
              accept=".csv"
              hidden
              onChange={handleFileChange}
            />
          </Button>
          <Typography
            variant="body2"
            sx={{
              flexGrow: 1,
              fontStyle: file ? 'normal' : 'italic',
              color: file ? 'text.primary' : 'text.secondary',
              userSelect: 'none',
            }}
          >
            {file ? file.name : 'No file selected'}
          </Typography>
        </Box>

        {loading && (
          <Box mb={2}>
            <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
            <Typography variant="caption" sx={{ mt: 0.5, fontWeight: '600', color: 'primary.main' }}>
              {progress}%
            </Typography>
          </Box>
        )}

        <Button
          variant="contained"
          type="submit"
          disabled={loading}
          size="medium"
          fullWidth
          sx={{
            fontSize: '0.9rem',
            py: 1.25,
            fontWeight: '700',
            borderRadius: 2,
            backgroundColor: '#1976d2',
            boxShadow: '0 5px 10px rgb(25 118 210 / 0.3)',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: '#115293',
              boxShadow: '0 10px 20px rgb(17 82 147 / 0.5)',
              transform: 'scale(1.05)',
            },
            '&:disabled': {
              backgroundColor: '#90caf9',
              boxShadow: 'none',
              cursor: 'not-allowed',
            },
          }}
        >
          {loading ? 'Uploading...' : 'Upload'}
        </Button>
      </form>

      {status && (
        <Typography
          variant="body2"
          color={status.startsWith('Error') ? 'error' : 'success.main'}
          sx={{ mt: 2, fontWeight: 600, letterSpacing: 0.3 }}
        >
          {status}
        </Typography>
      )}
    </Paper>
  );
};

export default CsvUploader;
